import React from 'react';
import { useReactFlow } from 'reactflow';
import { Grid } from '@mui/material';
import { SparkDrawer } from 'sharedComponents/UI';
import {
  SparkInputValuesContainer,
  SparkStepDrawerContent,
  SparkOutputBoardContainer,
} from './RightDrawerContent';
import { useReactFlowStore } from 'components/CreateSpark/SparkBuilderCanvas/hooks';
import { SPARK_DRAWER_TYPES } from 'constants/common';
import { useSparkBuilderStore } from 'stores';

type RightDrawerContainerProps = {
  open: boolean;
  headerHeight: number;
};

function RightDrawerContainer({
  open,
  headerHeight,
}: RightDrawerContainerProps) {
  const steps = useSparkBuilderStore((store) => store.sparkSteps.data);

  const setSelectedStep = useSparkBuilderStore(
    (store) => store.builderSideBarActions.setSelectedStep
  );
  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );
  const { addSelectedNodes } = useReactFlowStore();
  const { fitView, getZoom } = useReactFlow();

  const handleContinueClick = React.useCallback(() => {
    const currentStepOrder = steps?.find((step) => step.id === selectedStep?.id)
      ?.attributes.stepOrder;

    if (!currentStepOrder) return;
    const nextStepByOrder = steps?.find(
      (steps) => steps.attributes.stepOrder === currentStepOrder + 1
    );

    const currentZoom = getZoom();

    if (!nextStepByOrder) {
      setSelectedStep({ id: '', type: 'output' });
      addSelectedNodes([]);
      return;
    }
    setSelectedStep({ type: 'sparkStep', id: nextStepByOrder.id });
    addSelectedNodes([nextStepByOrder.id]);

    fitView({
      nodes: [{ id: nextStepByOrder.id }],
      duration: 200,
      maxZoom: currentZoom,
    });
  }, [
    addSelectedNodes,
    fitView,
    getZoom,
    steps,
    selectedStep?.id,
    setSelectedStep,
  ]);

  const onClose = React.useCallback(() => {
    setSelectedStep(null);
    addSelectedNodes([]);
  }, [addSelectedNodes, setSelectedStep]);

  const inputValues = selectedStep?.type === SPARK_DRAWER_TYPES.input;
  const outputValue = selectedStep?.type === SPARK_DRAWER_TYPES.output;
  const getRightDrawerContent = React.useMemo(() => {
    if (!selectedStep) {
      return <></>;
    }

    if (inputValues) {
      return <SparkInputValuesContainer onClose={onClose} />;
    }
    if (outputValue) {
      return <SparkOutputBoardContainer onClose={onClose} />;
    }

    return (
      <SparkStepDrawerContent
        handleContinueClick={handleContinueClick}
        sparkStepId={selectedStep?.id}
        onClose={onClose}
      />
    );
  }, [handleContinueClick, inputValues, onClose, outputValue, selectedStep]);

  const shouldRenderFullWidth = inputValues;

  return (
    <SparkDrawer
      variant='permanent'
      anchor='right'
      open={open}
      openWidth={!!outputValue ? 950 : 500}
      resizable={true}
      fullWidth={shouldRenderFullWidth}
    >
      <Grid container height='100%'>
        {getRightDrawerContent}
      </Grid>
    </SparkDrawer>
  );
}

export default RightDrawerContainer;
